/**
 * Creative Identity Service
 * Transforme les fingerprints techniques en identifiants mémorables et créatifs
 *
 * Principe : Hash technique -> Identifiant créatif unique
 * Exemple : 3825f5c79427a89ff670a564d9202792 -> "Lion Céleste #42"
 */

import i18n from "@core/i18n";
import { logger } from "./logger";

/** Slug stable d'un nom créatif → clé i18n de sa description. */
const identitySlug = (name: string): string =>
  name
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_+$/g, "");

export interface CreativeIdentity {
  /** Identifiant créatif affiché à l'utilisateur */
  displayName: string;
  /** Hash technique pour le backend */
  technicalHash: string;
  /** Catégorie créative (animal, constellation, element, hero) */
  category: "animal" | "constellation" | "element" | "hero";
  /** Numéro unique dans la catégorie */
  number: number;
  /** Métadonnées pour personnalisation */
  metadata: {
    color: string;
    emoji: string;
    description: string;
  };
}

// Dictionnaires créatifs
const ANIMALS = [
  {
    name: "Lion",
    color: "doré",
    emoji: "🦁",
    description: "Majesté et courage",
  },
  {
    name: "Aigle",
    color: "céleste",
    emoji: "🦅",
    description: "Vision et liberté",
  },
  {
    name: "Loup",
    color: "lunaire",
    emoji: "🐺",
    description: "Instinct et loyauté",
  },
  {
    name: "Renard",
    color: "argenté",
    emoji: "🦊",
    description: "Ruse et agilité",
  },
  {
    name: "Papillon",
    color: "émeraude",
    emoji: "🦋",
    description: "Transformation et légèreté",
  },
  {
    name: "Dragon",
    color: "solaire",
    emoji: "🐉",
    description: "Sagesse et puissance",
  },
  {
    name: "Phénix",
    color: "crimson",
    emoji: "🔥",
    description: "Renaissance et espoir",
  },
  {
    name: "Tigre",
    color: "orange",
    emoji: "🐅",
    description: "Force et passion",
  },
];

const CONSTELLATIONS = [
  {
    name: "Étoile Polaire",
    color: "arctique",
    emoji: "⭐",
    description: "Guide et constance",
  },
  {
    name: "Nébuleuse d'Orion",
    color: "cosmique",
    emoji: "🌌",
    description: "Mystère et beauté",
  },
  {
    name: "Galaxie d'Andromède",
    color: "violette",
    emoji: "🌠",
    description: "Élégance et distance",
  },
  {
    name: "Sirius",
    color: "azur",
    emoji: "⭐",
    description: "Brillance et éclat",
  },
  {
    name: "Vénus",
    color: "rosée",
    emoji: "🪐",
    description: "Amour et harmonie",
  },
  {
    name: "Mars",
    color: "rouge",
    emoji: "🪐",
    description: "Énergie et action",
  },
  {
    name: "Jupiter",
    color: "royal",
    emoji: "🪐",
    description: "Sagesse et protection",
  },
  {
    name: "Saturne",
    color: "anneau",
    emoji: "🪐",
    description: "Structure et discipline",
  },
];

const ELEMENTS = [
  {
    name: "Cascade",
    color: "émeraude",
    emoji: "💧",
    description: "Fluidité et pureté",
  },
  {
    name: "Volcan",
    color: "rubis",
    emoji: "🌋",
    description: "Création et destruction",
  },
  {
    name: "Forêt",
    color: "lunaire",
    emoji: "🌳",
    description: "Croissance et mystère",
  },
  {
    name: "Océan",
    color: "saphir",
    emoji: "💧",
    description: "Profondeur et calme",
  },
  {
    name: "Montagne",
    color: "granit",
    emoji: "⛰️",
    description: "Stabilité et hauteur",
  },
  {
    name: "Désert",
    color: "doré",
    emoji: "☀️",
    description: "Endurance et clarté",
  },
  {
    name: "Tempête",
    color: "gris",
    emoji: "⚡",
    description: "Puissance et changement",
  },
  {
    name: "Aurore",
    color: "iridescent",
    emoji: "🌅",
    description: "Magie et renouveau",
  },
];

const HEROES = [
  {
    name: "Explorateur Cosmique",
    color: "stellaire",
    emoji: "🧑‍🚀",
    description: "Curiosité et aventure",
  },
  {
    name: "Gardien des Sondages",
    color: "protecteur",
    emoji: "🛡️",
    description: "Sécurité et fiabilité",
  },
  {
    name: "Voyageur Temporel",
    color: "quantique",
    emoji: "⏰",
    description: "Précision et vision",
  },
  {
    name: "Architecte d'Idées",
    color: "créatif",
    emoji: "📐",
    description: "Imagination et structure",
  },
  {
    name: "Messager des Étoiles",
    color: "céleste",
    emoji: "📨",
    description: "Communication et sagesse",
  },
  {
    name: "Chroniqueur des Rêves",
    color: "onirique",
    emoji: "💭",
    description: "Inspiration et créativité",
  },
  {
    name: "Maître du Hasard",
    color: "probabiliste",
    emoji: "🎲",
    description: "Chance et stratégie",
  },
  {
    name: "Cavalier des Nuages",
    color: "éthéré",
    emoji: "☁️",
    description: "Légèreté et perspective",
  },
];

/**
 * Convertit un hash hexadécimal en nombre déterministe
 */
function hashToNumber(hash: string, max: number): number {
  let num = 0;
  for (let i = 0; i < Math.min(hash.length, 16); i++) {
    const char = hash[i];
    const value = parseInt(char, 16);
    if (!isNaN(value)) {
      // Réduction à chaque chiffre : le cumul brut dépasse 2^53 au-delà de
      // 13 chiffres hexadécimaux et perdrait la précision (index quasi constant).
      num = (num * 16 + value) % max;
    }
  }
  return num;
}

/**
 * Génère un identifiant créatif à partir du hash technique
 */
export function generateCreativeIdentity(
  technicalHash: string,
): CreativeIdentity {
  try {
    // Sélectionner la catégorie (4 catégories)
    const categories: Array<"animal" | "constellation" | "element" | "hero"> = [
      "animal",
      "constellation",
      "element",
      "hero",
    ];
    const categoryIndex = hashToNumber(technicalHash, categories.length);
    const category = categories[categoryIndex];

    // Sélectionner l'élément dans la catégorie
    let elements;
    switch (category) {
      case "animal":
        elements = ANIMALS;
        break;
      case "constellation":
        elements = CONSTELLATIONS;
        break;
      case "element":
        elements = ELEMENTS;
        break;
      case "hero":
        elements = HEROES;
        break;
    }

    const elementIndex = hashToNumber(
      technicalHash.substring(8),
      elements.length,
    );
    const element = elements[elementIndex];

    // Générer un nombre unique (1-999)
    const number = hashToNumber(technicalHash.substring(16), 999) + 1;

    // Construire le displayName
    const displayName = `${element.name} ${element.color.charAt(0).toUpperCase() + element.color.slice(1)} #${number}`;

    const identity: CreativeIdentity = {
      displayName,
      technicalHash,
      category,
      number,
      metadata: {
        color: element.color,
        emoji: element.emoji,
        // Description localisée (le name/color restent un codename stylisé).
        description: i18n.t(
          `creative_identity.desc.${identitySlug(element.name)}`,
          {
            defaultValue: element.description,
          },
        ),
      },
    };

    logger.info("Creative identity generated", "general", {
      displayName,
      category,
      number,
      technicalHash: technicalHash.substring(0, 16) + "...",
    });

    return identity;
  } catch (error) {
    logger.error("Failed to generate creative identity", "general", {
      error,
      technicalHash,
    });

    // Fallback ultra-simple
    return {
      displayName: i18n.t("creativeIdentity.fallback.displayName", {
        number: hashToNumber(technicalHash, 9999) + 1,
      }),
      technicalHash,
      category: "animal",
      number: hashToNumber(technicalHash, 999) + 1,
      metadata: {
        color: "neutre",
        emoji: "👤",
        description: i18n.t("creativeIdentity.fallback.description"),
      },
    };
  }
}

/**
 * Récupère ou génère une identité créative depuis le cache
 */
export async function getCachedCreativeIdentity(
  technicalHash: string,
): Promise<CreativeIdentity> {
  const CACHE_KEY = "__dd_creative_identity";
  const CACHE_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 jours

  try {
    // Vérifier le cache
    const cachedRaw = localStorage.getItem(CACHE_KEY);
    if (cachedRaw) {
      try {
        const cached = JSON.parse(cachedRaw);
        if (cached?.technicalHash === technicalHash && cached?.identity) {
          const age = Date.now() - new Date(cached.timestamp).getTime();
          if (age < CACHE_DURATION) {
            logger.debug("Using cached creative identity", "general", {
              displayName: cached.identity.displayName,
              ageHours: Math.round(age / 1000 / 60 / 60),
            });
            return cached.identity;
          }
        }
      } catch (error) {
        logger.warn("Invalid creative identity cache", "general", { error });
      }
    }

    // Générer nouvelle identité
    const identity = generateCreativeIdentity(technicalHash);

    // Mettre en cache
    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({
        technicalHash,
        identity,
        timestamp: new Date().toISOString(),
      }),
    );

    return identity;
  } catch (error) {
    logger.error("Failed to get cached creative identity", "general", {
      error,
      technicalHash,
    });
    return generateCreativeIdentity(technicalHash);
  }
}

/**
 * Formate l'identité pour l'affichage dans l'UI
 */
export function formatIdentityForDisplay(
  identity: CreativeIdentity,
  options: {
    showEmoji?: boolean;
    showNumber?: boolean;
    showColor?: boolean;
  } = {},
): string {
  const { showEmoji = true, showNumber = true, showColor = true } = options;

  let display = "";

  if (showEmoji) {
    display += `${identity.metadata.emoji} `;
  }

  display += identity.displayName;

  return display;
}

/**
 * Génère une URL de profil avatar basée sur l'identité
 */
export function generateAvatarUrl(
  identity: CreativeIdentity,
  size: number = 64,
): string {
  // Utiliser une API de génération d'avatars avec les paramètres créatifs
  const baseUrl = "https://api.dicebear.com/7.x/avataaars/svg";
  const params = new URLSearchParams();
  params.set("seed", identity.technicalHash.substring(0, 16)); // Utiliser le hash pour la cohérence
  params.set("size", size.toString());
  // Personnaliser selon la catégorie
  if (identity.category === "animal") {
    params.set("accessories", "sunglasses");
  } else if (identity.category === "constellation") {
    params.set("accessories", "round");
  } else if (identity.category === "element") {
    params.set("accessories", "ear");
  } else if (identity.category === "hero") {
    params.set("accessories", "kurt");
  }

  return `${baseUrl}?${params.toString()}`;
}

/**
 * Vérifie si deux identités créatives correspondent au même hash technique
 */
export function isSameUser(
  identity1: CreativeIdentity,
  identity2: CreativeIdentity,
): boolean {
  return identity1.technicalHash === identity2.technicalHash;
}
